/**
 * URL scheme allowlist for client-side redirects.
 *
 * Defense-in-depth: even though our redirect targets come from the BFF
 * (server-controlled) or from React Router's `location.pathname` (browser-
 * controlled, never raw user input), we still validate the scheme before
 * assigning to `window.location.href` to neutralise any future regression
 * where an attacker-controlled value could reach a redirect.
 *
 * Rejects `javascript:`, `data:`, `vbscript:`, `file:`, etc. — which are
 * the only schemes that could turn a redirect into XSS.
 */

const ALLOWED_PROTOCOLS = new Set(['http:', 'https:']);
const FALLBACK_PATH = '/';

/**
 * Sanitise a redirect target before assigning to `window.location.href`.
 *
 * Accepts:
 *  - Same-origin paths (e.g. `/insights`, `/self-care/foo?bar=1`)
 *  - Absolute http/https URLs that share the current origin
 *
 * Rejects (returns FALLBACK_PATH):
 *  - `javascript:` / `data:` / `vbscript:` / other dangerous schemes
 *  - Protocol-relative URLs (`//evil.com`) which would change origin silently
 *  - Cross-origin absolute URLs (e.g. `https://evil.com/`) — open-redirect defence
 *  - Malformed URLs
 */
export const sanitizeRedirectTarget = (target: string | null | undefined): string => {
  if (!target || typeof target !== 'string') {
    return FALLBACK_PATH;
  }

  const trimmed = target.trim();
  if (!trimmed) {
    return FALLBACK_PATH;
  }

  // Reject protocol-relative URLs ("//evil.com/x") — they change origin.
  if (trimmed.startsWith('//')) {
    return FALLBACK_PATH;
  }

  // Same-origin path — must start with a single "/" and not be protocol-relative.
  if (trimmed.startsWith('/')) {
    return trimmed;
  }

  // Absolute URL — validate the scheme and enforce same-origin.
  try {
    const url = new URL(trimmed, window.location.origin);
    if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
      return FALLBACK_PATH;
    }
    // Reject cross-origin absolute URLs — open-redirect defence.
    // Same-origin paths are already handled above; an absolute URL that
    // reaches here must match the current origin to be safe.
    if (url.origin !== window.location.origin) {
      return FALLBACK_PATH;
    }
    return url.toString();
  } catch {
    return FALLBACK_PATH;
  }
};
